import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Palette, ShieldCheck, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { ArtworkCard } from "../components/ArtworkCard";
import { Seo } from "../components/Seo";

const HERO_BG =
  "https://images.pexels.com/photos/22765209/pexels-photo-22765209.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Home() {
  const [artworks, setArtworks] = useState([]);

  useEffect(() => {
    api.get("/artworks").then((res) => setArtworks(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="/" jsonLd={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "La Fondation des Artistes Handicapées de France",
        url: "https://lesartisteshandicap.fr",
        logo: "https://lesartisteshandicap.fr/icon-512.png",
        description: "Galerie et boutique d'art d'artistes handicapés de France.",
      }} />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.4em] text-[#60a5fa] mb-6"
          >
            Galerie d'art · France
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-white max-w-4xl tracking-tight"
          >
            L'art qui voit <span className="italic text-[#60a5fa]">au-delà</span> du regard.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display italic text-xl md:text-2xl text-[#60a5fa] mt-6 max-w-2xl"
            data-testid="home-subtitle"
          >
            « Là où il n'y a pas d'espoir, nous devons l'inventer. »
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-zinc-300 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
          >
            La Fondation des Artistes Handicapées de France révèle des œuvres expressionnistes,
            colorées et puissantes. Chaque tableau acheté soutient directement son créateur.
          </motion.p>

          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 max-w-xl border-l-2 border-[#2563EB] pl-6"
            data-testid="home-quote"
          >
            <p className="text-[#60a5fa] text-xs uppercase tracking-[0.3em] mb-3">
              On a besoin de vous pour créer
            </p>
            <p className="font-display text-2xl md:text-3xl italic text-white leading-snug">
              « Peu d'artistes ont réussi à sortir de l'ombre, mais certains comme Henri de
              Toulouse-Lautrec ont prouvé que le talent n'a pas de limites physiques. »
            </p>
          </motion.blockquote>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="flex flex-wrap gap-4 mt-12"
          >
            <Link
              to="/galerie"
              data-testid="hero-gallery-cta"
              className="group inline-flex items-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-4 text-sm tracking-wide transition-colors"
            >
              Découvrir les œuvres
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/a-propos"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white text-white px-8 py-4 text-sm tracking-wide transition-colors"
            >
              Notre mission
            </Link>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-3 gap-12 border-b border-white/10">
        {[
          { icon: Palette, title: "Œuvres uniques", text: "Des pièces originales, peintes à la main par des artistes en situation de handicap." },
          { icon: Heart, title: "Soutien direct", text: "Une part majeure de chaque vente revient directement à l'artiste créateur." },
          { icon: ShieldCheck, title: "Paiement sécurisé", text: "Carte bancaire via Stripe ou virement bancaire. Achat 100% sécurisé." },
        ].map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <v.icon className="h-7 w-7 text-[#60a5fa] mb-5" strokeWidth={1.4} />
            <h3 className="font-display text-2xl text-white mb-3">{v.title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">{v.text}</p>
          </motion.div>
        ))}
      </section>

      {/* FEATURED GALLERY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-3">La collection</p>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight">Œuvres à la une</h2>
          </div>
          <Link to="/galerie" className="hidden sm:inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {artworks.slice(0, 6).map((a, i) => (
            <ArtworkCard key={a.id} artwork={a} index={i} />
          ))}
        </div>
      </section>

      {/* IMPACT / CAGNOTTE CTA */}
      <section className="border-y border-white/10 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-3 gap-12 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#E11D48] mb-4">Notre impact</p>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight leading-tight mb-6">
              Chaque achat change une vie d'artiste.
            </h2>
            <p className="text-zinc-300 leading-relaxed max-w-xl">
              En achetant une œuvre ou en soutenant notre cagnotte, vous offrez aux artistes
              handicapés reconnaissance, autonomie et les moyens de continuer à créer. Une part majeure
              de chaque vente leur revient directement.
            </p>
            <div className="flex flex-wrap gap-10 mt-10">
              <div>
                <p className="font-display text-4xl text-[#60a5fa]">100%</p>
                <p className="text-zinc-500 text-sm mt-1">Œuvres originales</p>
              </div>
              <div>
                <p className="font-display text-4xl text-[#60a5fa]">+20</p>
                <p className="text-zinc-500 text-sm mt-1">Artistes mis en lumière</p>
              </div>
              <div>
                <p className="font-display text-4xl text-[#60a5fa]">∞</p>
                <p className="text-zinc-500 text-sm mt-1">Talent sans limites</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <a
              href="https://www.leetchi.com/fr/c/la-fondation-des-artistes-handicapes-1454665"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-leetchi-cta"
              className="inline-flex items-center justify-center gap-3 bg-[#E11D48] hover:bg-[#be123c] text-white px-8 py-4 text-sm tracking-wide transition-colors"
            >
              <HeartHandshake className="h-4 w-4" /> Soutenir la cagnotte
            </a>
            <Link to="/galerie" className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-white text-white px-8 py-4 text-sm tracking-wide transition-colors">
              Acheter une œuvre <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
